import { useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { EmptyState, useToast } from '@practium/ui';
import { useGetPlanCatalog, type PlanCatalogInterval, type PlanCatalogItem } from '@practium/features/api';
import { MyPlanSectionCard } from '../MyPlanSectionCard/MyPlanSectionCard';
import { CatalogPlanCard } from '../CatalogPlanCard/CatalogPlanCard';
import { computeYearlyDiscount, getPlanTierRank } from '../../utils/formatMyPlan';
import styles from './SwitchPlanCard.module.css';

interface SwitchPlanCardProps {
	onSelectPlan: (planId: string) => void;
	currentHierarchyRole?: string | null;
}

export const SwitchPlanCard = ({ onSelectPlan, currentHierarchyRole }: SwitchPlanCardProps) => {
	const toast = useToast();
	const [interval, setInterval] = useState<PlanCatalogInterval>('month');
	const { data: monthCatalog, isLoading: isLoadingMonth } = useGetPlanCatalog('month');
	const { data: yearCatalog, isLoading: isLoadingYear } = useGetPlanCatalog('year');
	const catalog = interval === 'year' ? yearCatalog : monthCatalog;
	const isLoading = interval === 'year' ? isLoadingYear : isLoadingMonth;

	const discount = useMemo(() => {
		const monthPlans = monthCatalog?.plans ?? [];
		const yearPlans = yearCatalog?.plans ?? [];
		for (const monthPlan of monthPlans) {
			const yearPlan = yearPlans.find((item) => item.name === monthPlan.name);
			if (yearPlan) return computeYearlyDiscount(monthPlan.price, yearPlan.price);
		}
		return 17;
	}, [monthCatalog, yearCatalog]);

	const currentRank = useMemo(() => {
		const currentPlan = catalog?.plans.find((plan) => plan.isCurrent);
		return getPlanTierRank(currentPlan?.hierarchy?.role ?? currentHierarchyRole);
	}, [catalog, currentHierarchyRole]);

	const resolveAction = (plan: PlanCatalogItem) => {
		if (plan.isCurrent) return 'current' as const;
		const rank = getPlanTierRank(plan.hierarchy?.role);
		if (currentRank > 0 && rank === currentRank) return 'downgrade' as const;
		if (rank > currentRank) return 'upgrade' as const;
		return 'downgrade' as const;
	};

	const handleSelect = (plan: PlanCatalogItem) => {
		if (resolveAction(plan) === 'downgrade') {
			toast.info('O downgrade de plano ainda não foi implementado.');
			return;
		}
		onSelectPlan(plan._id);
	};

	return (
		<MyPlanSectionCard
			title="Trocar de plano"
			subtitle="A mudança vale na hora e a diferença é calculada proporcionalmente."
			headerAction={
				<div className={styles.toggle}>
					<button
						type="button"
						className={`${styles.toggleBtn} ${interval === 'month' ? styles.toggleActive : ''}`}
						onClick={() => setInterval('month')}
					>
						Mensal
					</button>
					<button
						type="button"
						className={`${styles.toggleBtn} ${interval === 'year' ? styles.toggleActive : ''}`}
						onClick={() => setInterval('year')}
					>
						Anual <span className={styles.discount}>-{discount}%</span>
					</button>
				</div>
			}
		>
			<div className={styles.body}>
			{isLoading ? (
				<p className={styles.empty}>Carregando planos...</p>
			) : catalog?.plans.length ? (
				<div className={styles.grid}>
					{catalog.plans.map((plan) => (
						<CatalogPlanCard
							key={plan._id}
							plan={plan}
							action={resolveAction(plan)}
							onSelect={() => handleSelect(plan)}
						/>
					))}
				</div>
			) : (
				<EmptyState
					icon={<Layers size={28} />}
					title="Nenhum plano disponível"
					description="Não há planos para este intervalo de cobrança no momento."
				/>
			)}
			</div>
		</MyPlanSectionCard>
	);
};
